import { Injectable } from '@angular/core';
import {
  JOB_COMPLEXITY,
  TOGGLE_MULTIPLIERS,
  ELEVATED_WORK_OPTIONS,
  TUBING_TIERS,
  TUBING_BENDS,
  ADDED_DURATION_KEYWORDS,
} from '../config/estimation-config';
import {
  AppliedMultiplier,
  AddedDuration,
  NormsEstimate,
} from '../models/norms.model';

/** Raw selections gathered from the NORMs Lookup tab. */
export interface EstimateInput {
  taskId: string;
  taskName: string;
  craft: string;
  crewSize: number | null;
  baseHours: number;
  roundedBaseHours: number;
  complexityKey: string;
  restrictivePpe: boolean;
  confinedSpace: boolean;
  /** Key from ELEVATED_WORK_OPTIONS (e.g. 'none', 'lt20', '20to40'). */
  elevatedWork: string;
  /** norm_hours from the API response — used as the unit rate for tubing/conduit. */
  normHours: number;
  tubing?: {
    size: string;
    lengthFt: number;
    bends: number;
    /** API-provided bend unit rate (h per every 3 bends). Falls back to frontend config. */
    bendRateOverride?: number;
    /** API-provided bend rate ID, e.g. TUB01–TUB04. Falls back to frontend config. */
    bendIdOverride?: string;
  } | null;
  conduit?: { lengthFt: number } | null;
}

/**
 * Pure estimation math for NORMs-sourced tasks.
 *
 * DOMAIN RULES enforced here:
 *  - Multipliers are one-time factors: adjusted = base * product(selected).
 *  - Tubing is ADDED DURATION (tiered per-foot + bend add-on), never a multiplier.
 *  - Conduit is ADDED DURATION (per-foot), never a multiplier.
 *  - Added durations are summed AFTER the multiplier product is applied to base
 *    hours, because the multipliers describe the normalized task effort and the
 *    tubing/conduit are discrete extra scope on top of it.
 */
@Injectable({ providedIn: 'root' })
export class EstimationService {

  private round(n: number): number {
    return Math.round(n * 100) / 100;
  }

  /** Returns a fraction string; improper fractions shown as mixed numbers.
   *  e.g. toFraction(2,3) → '2/3', toFraction(4,3) → '1-1/3', toFraction(3,3) → '1'. */
  private toFraction(numerator: number, denominator: number): string {
    const gcd = (a: number, b: number): number => b === 0 ? a : gcd(b, a % b);
    const g = gcd(numerator, denominator);
    const n = numerator / g;
    const d = denominator / g;
    if (d === 1) return `${n}`;
    if (n > d) {
      const whole = Math.floor(n / d);
      const rem = n % d;
      return rem === 0 ? `${whole}` : `${whole}-${rem}/${d}`;
    }
    return `${n}/${d}`;
  }

  /** True when the task name matches a configured tubing keyword. */
  isTubingTask(taskName: string | undefined): boolean {
    return this.matchesAny(taskName, ADDED_DURATION_KEYWORDS.tubing);
  }

  /** True when the task name matches a configured conduit keyword. */
  isConduitTask(taskName: string | undefined): boolean {
    return this.matchesAny(taskName, ADDED_DURATION_KEYWORDS.conduit);
  }

  private matchesAny(taskName: string | undefined, keywords: string[]): boolean {
    if (!taskName) return false;
    const t = taskName.toLowerCase();
    return keywords.some(k => t.includes(k.toLowerCase()));
  }

  /** Tubing length + bend duration.
   *  normHours: API-returned length unit rate.
   *  bendRateOverride: API bend unit rate (h per 3 bends); falls back to config.
   *  bendIdOverride: API bend rate ID (e.g. TUB04); falls back to config.
   */
  computeTubing(
    size: string,
    lengthFt: number,
    bends: number,
    normHours: number,
    bendRateOverride?: number,
    bendIdOverride?: string,
  ): AddedDuration | null {
    const tier = TUBING_TIERS.find(t => t.size === size) ?? TUBING_TIERS[0];
    const len = Number(lengthFt) || 0;
    const nBends = Number(bends) || 0;
    if (len <= 0 && nBends <= 0) return null;

    const lengthHours = (len / tier.intervalFt) * normHours;
    const bendRate = bendRateOverride ?? TUBING_BENDS.bendUnitHours[size] ?? TUBING_BENDS.bendUnitHours['1/4"'];
    const effectiveBendId = bendIdOverride ?? tier.bendId;
    // Proportional like the length calculation — 2 bends ÷ 3 = 0.67 bend units
    const bendOccurrences = nBends > 0 ? nBends / TUBING_BENDS.bendInterval : 0;
    const bendHours = bendOccurrences * bendRate;
    const hours = this.round(lengthHours + bendHours);

    // Build detail lines: length calculation on one line, bends on another
    const lengthLine = `${len} ft ÷ ${tier.intervalFt} × ${normHours} h/unit = ${this.round(lengthHours)} h`;
    const bendLine = bendOccurrences > 0
      ? `${this.toFraction(nBends, TUBING_BENDS.bendInterval)} bend unit × ${bendRate} h = ${this.round(bendHours)} h`
      : '';
    const detail = lengthLine + (bendLine ? '\n' + bendLine : '');

    return {
      id: effectiveBendId,
      key: 'tubing',
      label: `Tubing (${size})`,
      hours,
      detail,
      confirmed: TUBING_BENDS.confirmed,
    };
  }

  /** Conduit duration. norm_hours is the API-returned rate (h/ft). */
  computeConduit(lengthFt: number, normHours: number): AddedDuration | null {
    const len = Number(lengthFt) || 0;
    if (len <= 0) return null;
    const hours = this.round(len * normHours);
    return {
      key: 'conduit',
      label: 'Conduit',
      hours,
      detail: `${len} ft × ${normHours} h/ft = ${hours} h`,
      confirmed: true,
    };
  }

  /** Build the full normalized estimate from raw selections. */
  computeEstimate(input: EstimateInput): NormsEstimate {
    const base = Number(input.baseHours) || 0;

    // ── Multipliers (one-time factors) ──
    const multipliers: AppliedMultiplier[] = [];

    const complexity = JOB_COMPLEXITY.find(c => c.key === input.complexityKey) ?? JOB_COMPLEXITY[0];
    multipliers.push({ id: complexity.id, key: 'jobComplexity', label: `Job Complexity (${complexity.label})`, factor: complexity.factor, confirmed: complexity.confirmed });

    const toggleState: Record<string, boolean> = {
      restrictivePpe: input.restrictivePpe,
      confinedSpace: input.confinedSpace,
    };
    for (const opt of TOGGLE_MULTIPLIERS) {
      if (toggleState[opt.key]) {
        multipliers.push({ id: opt.id, key: opt.key, label: opt.label, factor: opt.factor, confirmed: opt.confirmed });
      }
    }
    // Elevated Work — segmented selector; 'none' = no overhead
    const elevated = ELEVATED_WORK_OPTIONS.find(e => e.key === input.elevatedWork);
    if (elevated && elevated.key !== 'none') {
      multipliers.push({ id: elevated.id, key: 'elevatedWork', label: `Elevated Work (${elevated.label})`, factor: elevated.factor, confirmed: elevated.confirmed });
    }

    const multiplierProduct = this.round(multipliers.reduce((p, m) => p * m.factor, 1));
    const multipliedBase = base * multiplierProduct;

    // ── Added durations (tubing / conduit) ──
    const addedDurations: AddedDuration[] = [];
    if (input.tubing) {
      const t = this.computeTubing(
        input.tubing.size,
        input.tubing.lengthFt,
        input.tubing.bends,
        input.normHours,
        input.tubing.bendRateOverride,
        input.tubing.bendIdOverride,
      );
      if (t) addedDurations.push(t);
    }
    if (input.conduit) {
      const c = this.computeConduit(input.conduit.lengthFt, input.normHours);
      if (c) addedDurations.push(c);
    }
    const addedTotal = this.round(addedDurations.reduce((s, a) => s + a.hours, 0));

    const rawAdjusted = this.round(multipliedBase + addedTotal);
    // Round UP to the nearest 15-minute increment (0.25 h) per SME guidance (Josh Stitt, June 2026).
    const adjustedHours = rawAdjusted > 0
      ? this.round(Math.ceil(rawAdjusted / 0.25) * 0.25)
      : 0;
    // Per-resource duration: divide raw adjusted by crew size, then ceil to 0.25 h.
    const crew = input.crewSize && input.crewSize > 0 ? input.crewSize : null;
    const hoursPerResource = rawAdjusted > 0 && crew
      ? this.round(Math.ceil((rawAdjusted / crew) / 0.25) * 0.25)
      : adjustedHours > 0 ? adjustedHours : null;
    // Raw per-resource value WITHOUT the 0.25 h ceiling — the true library number.
    const rawHoursPerResource = rawAdjusted > 0
      ? this.round(crew ? rawAdjusted / crew : rawAdjusted)
      : null;
    const allConfirmed =
      multipliers.every(m => m.confirmed) && addedDurations.every(a => a.confirmed);

    const estimate: NormsEstimate = {
      taskId: input.taskId,
      taskName: input.taskName,
      craft: input.craft,
      crewSize: input.crewSize,
      baseHours: this.round(base),
      roundedBaseHours: this.round(input.roundedBaseHours || base),
      multipliers,
      multiplierProduct,
      addedDurations,
      addedTotal,
      adjustedHours,
      rawAdjustedHours: rawAdjusted,
      hoursPerResource,
      rawHoursPerResource,
      allConfirmed,
      referenceCode: '',
      maximoDescription: '',
      generatedAt: new Date().toISOString(),
    };

    estimate.referenceCode = this.buildReferenceCode(estimate);
    estimate.maximoDescription = this.buildMaximoDescription(estimate);
    return estimate;
  }

  /**
   * Compact, parseable reference code encoding task_id + every applied
   * multiplier + any added durations. Example:
   *   NORMS-CV-MED-300/JC=medium+PPE+CS+TUB(20ft,5b)+CON(12ft)=8.25h
   */
  buildReferenceCode(e: NormsEstimate): string {
    // Format per SME example: {taskId} {JCxxx} {PPExxx} {CSxxx} {EWxxx} [{TUBxx}...] TOTAL:{h}
    // IDs from column F of the SME Multipliers sheet.
    // Future: when the API returns multiplier_ids these config fallbacks are replaced.
    const parts: string[] = [];

    // Task ID (API task_id, e.g. PF035)
    parts.push(e.taskId || 'NA');

    // Job Complexity — always present
    const jc = e.multipliers.find(m => m.key === 'jobComplexity');
    parts.push(jc?.id ?? 'JC001');

    // PPE — PPE02 if restrictive selected, PPE01 (Standard) otherwise
    const ppe = e.multipliers.find(m => m.key === 'restrictivePpe');
    parts.push(ppe?.id ?? 'PPE01');

    // Confined Space — CS002 if confined selected, CS001 (Not confined) otherwise
    const cs = e.multipliers.find(m => m.key === 'confinedSpace');
    parts.push(cs?.id ?? 'CS001');

    // Elevated Work — always present (default EW001 = 0–20')
    const ew = e.multipliers.find(m => m.key === 'elevatedWork');
    parts.push(ew?.id ?? 'EW001');

    // Added durations (tubing / conduit)
    for (const a of e.addedDurations) {
      if (a.key === 'tubing') {
        const rawSize = a.label.match(/\(([^)]+)\)/)?.[1] ?? '';
        const tier = TUBING_TIERS.find(t => t.size === rawSize);
        const bendId = a.id ?? tier?.bendId ?? 'TUB';
        const sizeShort = rawSize.replace(/"/g, 'in');
        const lenMatch = a.detail.match(/^(\d+(?:\.\d+)?)\s*ft/);
        const len = lenMatch ? lenMatch[1] : '0';
        const bendUnitsMatch = a.detail.match(/([\d.]+)\s*bend\s*unit/);
        const bendUnits = bendUnitsMatch ? bendUnitsMatch[1] : '0';
        parts.push(`${bendId}:${sizeShort}@${len}ft,${bendUnits}u`);
      } else if (a.key === 'conduit') {
        const lenMatch = a.detail.match(/^(\d+(?:\.\d+)?)\s*ft/);
        const len = lenMatch ? lenMatch[1] : '0';
        parts.push(`CON:${len}ft`);
      }
    }

    return parts.join(' ');
  }

  /**
   * Canonical Maximo long-description in the required sequence:
   *   task -> factors -> total. This is the record planners copy into Maximo;
   * the displayed adjusted duration must always match the total here.
   */
  buildMaximoDescription(e: NormsEstimate): string {
    const lines: string[] = [];
    lines.push('NEST NORMALIZED ESTIMATE — do not edit duration manually');
    lines.push('========================================================');
    lines.push(`Task: ${e.taskName}${e.taskId ? ` [${e.taskId}]` : ''}`);
    lines.push(`Craft: ${e.craft}${e.crewSize != null ? `   Crew: ${e.crewSize}` : ''}`);
    lines.push(`Base NORM effort: ${e.baseHours} h (rounded: ${e.roundedBaseHours} h)`);
    lines.push('');
    lines.push('Applied factors (one-time multipliers):');
    for (const m of e.multipliers) {
      lines.push(`  - ${m.label}: x${m.factor}${m.confirmed ? '' : '  [PLACEHOLDER — pending SME]'}`);
    }
    lines.push(`  = multiplier product x${e.multiplierProduct}  ->  ${this.round(e.baseHours * e.multiplierProduct)} h`);
    if (e.addedDurations.length > 0) {
      lines.push('');
      lines.push('Added durations (not multiplied):');
      for (const a of e.addedDurations) {
        lines.push(`  - ${a.label}: +${a.hours} h  (${a.detail})${a.confirmed ? '' : '  [PLACEHOLDER — pending SME]'}`);
      }
      lines.push(`  = added total +${e.addedTotal} h`);
    }
    lines.push('');
    lines.push(`ADJUSTED TOTAL: ${e.adjustedHours} h (task)`);
    if (e.hoursPerResource != null && e.crewSize && e.crewSize > 1) {
      lines.push(`PER RESOURCE:   ${e.hoursPerResource} h × ${e.crewSize} crew`);
    }
    lines.push(`Reference Code: see the "Reference Code" field above — use its Copy button`);
    lines.push(`Generated: ${e.generatedAt} by NEST UI`);
    return lines.join('\n');
  }
}
